/**
 * Pure helpers behind the Discover Workspaces page (Figma 10216:3878):
 * toolbar filters, the sort select, and the "Members" summary on each card.
 * Everything here works on the `GET /workspaces/discover` payload
 * (`{ workspaces, people }`) and never touches the network or React.
 */

export const STATUS_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'mine', label: 'My workspaces' },
    { id: 'pending', label: 'Pending requests' },
];

export const SORT_OPTIONS = [
    { id: 'newest', label: 'Newest first' },
    { id: 'oldest', label: 'Oldest first' },
    { id: 'name-desc', label: 'Alphabetical (Z-A)' },
    { id: 'name-asc', label: 'Alphabetical (A-Z)' },
    { id: 'members-asc', label: 'Members count (Low to High)' },
    { id: 'members-desc', label: 'Members count (High to Low)' },
];

export const DEFAULT_SORT = 'newest';

/** How many member avatars a card shows before "and N more" (Figma 10219:24475). */
export const MAX_STACK_AVATARS = 4;

export const EMPTY_FILTERS = Object.freeze({ query: '', userEmail: '', ownerEmail: '', status: 'all' });

const normalize = (value) => String(value || '').trim().toLowerCase();

/** Display name for an email: the profile name when known, else the address itself. */
export function personLabel(email, people) {
    const name = people?.[email]?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : email || '';
}

/** First name for the card summary ("Danny, Shimrit, ... and 12 more"); the mailbox name when no profile name. */
export function personFirstName(email, people) {
    const name = people?.[email]?.name;
    if (typeof name === 'string' && name.trim()) return name.trim().split(/\s+/)[0];
    return String(email || '').split('@')[0];
}

/** Role shown on a card or member row: "Owner" beats the stored role. */
export function displayRole(row) {
    if (!row) return null;
    if (row.is_owner) return 'Owner';
    return row.my_role || row.role || null;
}

/**
 * Apply the toolbar filters.  `query` matches the workspace name
 * (case-insensitive substring); `userEmail` keeps workspaces that person is
 * a member of; `ownerEmail` keeps workspaces that person owns; `status` is
 * one of `STATUS_FILTERS` ("mine" = the caller is a member, "pending" = the
 * caller has a pending request).
 */
export function filterWorkspaces(workspaces, filters = EMPTY_FILTERS) {
    const query = normalize(filters.query);
    const userEmail = normalize(filters.userEmail);
    const ownerEmail = normalize(filters.ownerEmail);
    const status = filters.status || 'all';
    return (workspaces || []).filter((ws) => {
        if (query && !normalize(ws.name).includes(query)) return false;
        if (userEmail && !(ws.member_emails || []).some((email) => normalize(email) === userEmail)) return false;
        if (ownerEmail && normalize(ws.owner_email) !== ownerEmail) return false;
        if (status === 'mine' && !ws.my_role) return false;
        if (status === 'pending' && !ws.join_request) return false;
        return true;
    });
}

const createdMs = (ws) => {
    const ms = Date.parse(ws.created_at || '');
    return Number.isFinite(ms) ? ms : 0;
};

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });

const COMPARATORS = {
    newest: (a, b) => createdMs(b) - createdMs(a) || byName(a, b),
    oldest: (a, b) => createdMs(a) - createdMs(b) || byName(a, b),
    'name-asc': byName,
    'name-desc': (a, b) => byName(b, a),
    'members-asc': (a, b) => (a.member_count || 0) - (b.member_count || 0) || byName(a, b),
    'members-desc': (a, b) => (b.member_count || 0) - (a.member_count || 0) || byName(a, b),
};

/** Stable sort by one of `SORT_OPTIONS`; unknown ids fall back to the default. */
export function sortWorkspaces(workspaces, sortId = DEFAULT_SORT) {
    const compare = COMPARATORS[sortId] || COMPARATORS[DEFAULT_SORT];
    return [...(workspaces || [])].sort(compare);
}

const uniqueSortedPeople = (emails, people) => {
    const seen = new Set();
    const rows = [];
    emails.forEach((email) => {
        const key = normalize(email);
        if (!key || seen.has(key)) return;
        seen.add(key);
        rows.push({ email: key, label: personLabel(key, people) });
    });
    return rows.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

/** Options for "Filter by user name": everyone who is a member of any listed workspace. */
export function peopleOptions(workspaces, people) {
    return uniqueSortedPeople((workspaces || []).flatMap((ws) => ws.member_emails || []), people);
}

/** Options for "Filter by owner": the owners of the listed workspaces. */
export function ownerOptions(workspaces, people) {
    return uniqueSortedPeople((workspaces || []).map((ws) => ws.owner_email), people);
}

/**
 * The emails a card's avatar stack shows.  The person picked in "Filter by
 * user name" / "Filter by owner" is moved into the first slot so they are
 * always visible; the rest keep the backend order (owner first).
 */
export function orderMembersForStack(memberEmails, highlightEmail) {
    const emails = [...(memberEmails || [])];
    const key = normalize(highlightEmail);
    if (!key) return emails;
    const index = emails.findIndex((email) => normalize(email) === key);
    if (index <= 0) return emails;
    const [picked] = emails.splice(index, 1);
    return [picked, ...emails];
}

/**
 * "Danny, Shimrit, Tolik, Eygeni and 12 more" for a card.  `total` is the
 * workspace's member count (it may exceed the listed emails); `more` is what
 * the underlined suffix shows and is 0 when everyone fits in the stack.
 */
export function membersSummary(memberEmails, people, total, max = MAX_STACK_AVATARS) {
    const shown = (memberEmails || []).slice(0, max);
    const names = shown.map((email) => personFirstName(email, people));
    const count = Math.max(Number(total) || 0, (memberEmails || []).length);
    const more = Math.max(count - shown.length, 0);
    let text = '';
    if (more > 0 || names.length <= 1) {
        text = names.join(', ');
    } else {
        text = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    }
    return { shown, text, more };
}

/** `true` when any toolbar filter deviates from the defaults. */
export function hasActiveFilters(filters) {
    return Boolean(
        normalize(filters?.query) || normalize(filters?.userEmail) || normalize(filters?.ownerEmail)
            || (filters?.status && filters.status !== 'all'),
    );
}
